<template>
  <v-container>
    <div class="d-flex flex-wrap justify-between align-center" style="gap: 10px">
      <h1>Activity Logs</h1>
    </div>
    <v-card>
      <v-toolbar color="primary" density="comfortable" flat>
        <div class="d-flex align-center text-white ml-2" style="gap: 12px">
          <v-checkbox
            v-for="opt in ACTIVITY_OPTIONS"
            :key="opt.value"
            v-model="selectedActivities"
            class="text-caption"
            :value="opt.value"
            density="comfortable"
            hide-details
            :label="opt.label"
          />
        </div>
        <v-spacer></v-spacer>
        <v-text-field
          v-if="!$vuetify.display.mdAndDown"
          ref="searchRef"
          v-model="searchQuery"
          placeholder="Search by user or description"
          clearable
          @click:clear="onClearSearch"
          prepend-inner-icon="mdi-magnify"
          hide-details
          density="compact"
          variant="solo"
          class="search-in-bar mr-2"
          @keyup.enter="handleSearch"
        ></v-text-field>
      </v-toolbar>
      <div class="px-2 pt-2 mb-2" v-if="$vuetify.display.mdAndDown">
        <v-text-field
          ref="searchRef"
          v-model="searchQuery"
          placeholder="Search by user or description"
          clearable
          @click:clear="onClearSearch"
          prepend-inner-icon="mdi-magnify"
          hide-details
          density="comfortable"
          variant="solo"
          style="width: 100%; max-width: 100%"
          @keyup.enter="handleSearch"
        ></v-text-field>
      </div>
    </v-card>
    <v-data-table
      :headers="headers"
      :items="activities"
      :items-length="totalItems"
      :items-per-page="itemsPerPage"
      :page="currentPage"
      :loading="loading"
      :server-items-length="totalItems"
      :items-per-page-options="itemsPerPageOptions"
      item-value="timestamp"
      class="elevation-1 sticky-header striped-rows even-cols"
      :style="{ '--even-col-count': headers.length }"
      @update:options="loadItems"
    >
      <template #item.activity="{ item }">
        <v-chip size="small" :color="getActivityColor(item.activity)" variant="tonal">
          {{ toCapitalCase(item.activity) }}
        </v-chip>
      </template>
      <template #item.user="{ item }">
        <div>
          <span class="font-weight-bold text-dark">{{ item.user }}</span>
          <span class="text-caption text-grey ml-2">{{ item.metadata?.userType || '' }}</span>
        </div>
      </template>
      <template #item.timestamp="{ item }">
        {{ formatTimestamp(item.timestamp) }}
      </template>
      <template #bottom>
        <div class="d-flex justify-space-between align-center pa-2">
          <div class="text-caption text-medium-emphasis">Showing {{ getDisplayedRange() }}</div>
          <div class="d-flex align-center" style="gap: 16px">
            <v-btn
              :disabled="!hasPreviousPage"
              variant="text"
              density="comfortable"
              @click="loadPreviousPage"
            >
              <v-icon start>mdi-chevron-left</v-icon>
              Previous
            </v-btn>
            <v-btn
              :disabled="!hasNextPage"
              variant="text"
              density="comfortable"
              @click="loadNextPage"
            >
              Next
              <v-icon end>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
      </template>
    </v-data-table>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { debounce } from 'lodash'
import { getLoggedData, VerifyAdmin } from '@/api/accountsAdmin'

const activities = ref<any[]>([])
const searchQuery = ref('')
const searchRef = ref()
const loading = ref(true)
const ACTIVITY_OPTIONS = [
  { value: 'LOGIN_DETAILS', label: 'Login' },
  { value: 'ADD_PRODUCT', label: 'Add Product' },
  { value: 'UPDATE_PRODUCT', label: 'Update Product' },
  { value: 'CREATE_ACCOUNT', label: 'Create Account' },
  { value: 'UPDATE_ACCOUNT', label: 'Update Account' },
]
const selectedActivities = ref<string[]>(ACTIVITY_OPTIONS.map((o) => o.value))

const itemsPerPage = ref(50)
const currentPage = ref(1)
const totalItems = ref(0)
const lastEvaluatedKey = ref<string | null>(null)
const pageHistory = ref<Array<{ page: number; lastEvaluatedKey: string | null }>>([])

const itemsPerPageOptions = [
  { value: 10, title: '10' },
  { value: 25, title: '25' },
  { value: 50, title: '50' },
]

const headers = [
  { title: 'Activity', value: 'activity', sortable: true, width: '20%' },
  { title: 'User', value: 'user', sortable: true, width: '20%' },
  { title: 'Timestamp', value: 'timestamp', sortable: true, width: '20%' },
  { title: 'Description', value: 'description', sortable: false, width: '40%' },
]

const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value))
const hasNextPage = computed(() => lastEvaluatedKey.value !== null)
const hasPreviousPage = computed(() => currentPage.value > 1)

function toCapitalCase(input: string): string {
  return input
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}
const getDisplayedRange = () => {
  const start = (currentPage.value - 1) * itemsPerPage.value + 1
  const end = Math.min(currentPage.value * itemsPerPage.value, totalItems.value)
  return `${start}-${end} of ${totalItems.value}`
}

async function loadActivityLogs(
  options: { page?: number; lastEvaluatedKey?: string | null; isPrevious?: boolean } = {}
) {
  try {
    loading.value = true
    const params: any = {
      limit: itemsPerPage.value,
      user: String(searchQuery.value || '').trim() || undefined,
    }
    const selected = [...selectedActivities.value]
    if (selected.length === 1) {
      params.activity = selected[0]
      const data = await getLoggedData(params)
      activities.value = data?.activities || []
      totalItems.value = data?.count || 0
      lastEvaluatedKey.value = data?.pagination?.nextPageToken || null
    } else if (selected.length === 0) {
      activities.value = []
      totalItems.value = 0
      lastEvaluatedKey.value = null
      return
    } else {
      const results = await Promise.all(
        selected.map((act) => getLoggedData({ ...params, activity: act }))
      )
      const merged: any[] = results.flatMap((r: any) => r?.activities || [])
      merged.sort(
        (a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )
      activities.value = merged.slice(0, itemsPerPage.value)
      totalItems.value = merged.length
      lastEvaluatedKey.value = null
    }
    if (options.page && !options.isPrevious) {
      pageHistory.value.push({
        page: options.page,
        lastEvaluatedKey: options.lastEvaluatedKey || null,
      })
    }
  } catch (e) {
    activities.value = []
    totalItems.value = 0
    lastEvaluatedKey.value = null
  } finally {
    loading.value = false
  }
}

const loadItems = (options: any) => {
  if (options.page !== currentPage.value || options.itemsPerPage !== itemsPerPage.value) {
    if (options.itemsPerPage !== itemsPerPage.value) {
      itemsPerPage.value = options.itemsPerPage
      currentPage.value = 1
      pageHistory.value = []
      loadActivityLogs({ page: 1, lastEvaluatedKey: null })
    }
  }
}

const loadNextPage = () => {
  if (hasNextPage.value) {
    currentPage.value++
    loadActivityLogs({ page: currentPage.value, lastEvaluatedKey: lastEvaluatedKey.value })
  }
}
const loadPreviousPage = () => {
  if (hasPreviousPage.value) {
    currentPage.value--
    const previousState = pageHistory.value.find((item) => item.page === currentPage.value)
    const keyForPreviousPage = previousState ? previousState.lastEvaluatedKey : null
    pageHistory.value = pageHistory.value.filter((item) => item.page < currentPage.value)
    loadActivityLogs({
      page: currentPage.value,
      lastEvaluatedKey: keyForPreviousPage,
      isPrevious: true,
    })
  }
}

const handleSearch = debounce(() => {
  currentPage.value = 1
  pageHistory.value = []
  loadActivityLogs({ page: 1, lastEvaluatedKey: null })
}, 500)
const onClearSearch = () => {
  searchQuery.value = ''
  handleSearch()
}
const getActivityColor = (activity: string) => {
  switch (activity) {
    case 'LOGIN_DETAILS':
      return 'green'
    case 'CREATE_ACCOUNT':
      return 'blue'
    case 'ADD_PRODUCT':
      return 'orange'
    case 'UPDATE_PRODUCT':
      return 'amber'
    case 'UPDATE_ACCOUNT':
      return 'purple'
    default:
      return 'grey'
  }
}
const formatTimestamp = (t: string) => {
  if (!t) return 'N/A'
  const d = new Date(t)
  return d
    .toLocaleString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: 'UTC',
    })
    .replace(' at', ',')
}

onMounted(() => {
  VerifyAdmin()
  loadActivityLogs({ page: 1, lastEvaluatedKey: null })
})
watch(selectedActivities, () => {
  currentPage.value = 1
  pageHistory.value = []
  loadActivityLogs({ page: 1, lastEvaluatedKey: null })
})
watch(searchQuery, () => {
  handleSearch()
})
watch(loading, (val, oldVal) => {
  if (oldVal && !val)
    setTimeout(() => {
      try {
        searchRef.value?.focus?.()
      } catch {}
    }, 0)
})
</script>

<style scoped>
.search-in-bar {
  max-width: 280px;
  opacity: 0.85;
}
@media (max-width: 960px) {
  .search-in-bar {
    max-width: 100%;
    width: 100%;
  }
}
.search-in-bar:hover,
.search-in-bar:focus-within {
  opacity: 1;
}
::deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.even-cols :deep(.v-data-table__wrapper table) {
  table-layout: fixed;
}
.even-cols :deep(th),
.even-cols :deep(td) {
  width: calc(100% / var(--even-col-count, 5));
}
::deep(.striped-rows .v-data-table__tr:nth-child(even)) {
  background-color: #f8f9fa;
}
::deep(.striped-rows .v-data-table__tr:hover) {
  background-color: #e9ecef !important;
}
::deep(.v-data-table tbody tr:hover) {
  background-color: #f0f0f0 !important;
}
.text-dark {
  color: #212121 !important;
}
.text-grey {
  color: #757575 !important;
}
::deep(.v-data-table__bottom) {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  background-color: #fafafa;
}
</style>
